import { actions } from 'react-redux-form';
import { httpGet } from '../../../../utils/api.utils';
import { DEFAULT_TABLE_RESULT_MODEL } from '../../../../constants/external-constants';
import {
    getInventoryCircuitPlanDetails,
    getCircuitOrderPlanDetails,
    getInventoryPlanTypesByVendorByCircuitType,
    getInventoryCircuitPlanComponents,
    deleteInventoryCircuitPlanDetails,
    addInventoryCircuitPlanDetails,
    resetInventoryCircuitPlanDetails,
    planDetailsFormModel,
    saveCheckedInventoryCircuitPlanComponents,
    setPlanLoading
} from "../../../../constants/model-reducers/external/inventory/circuit-details/details-view/add-plan-details.settings";

export const planDetailsFormInitialState = planDetailsFormModel;

const defaultState = () => ({
    circuitPlans: [],
    planComponents: DEFAULT_TABLE_RESULT_MODEL,
    planDetails: {
        datatable: []
    },
    checkedItems: [],
    circuitPlansPage: 1,
    hasMoreCircuitPlans: true,
    isCircuitPlansLoading: false,
});

const initialState = defaultState();

export const addPlanDetailActions = {
    getInventoryCircuitPlanDetails: (circuitID) => async (dispatch, state) => {
        var result = await httpGet('external/inventory/getInventoryCircuitPlanDetails/' + circuitID);

        dispatch({
            type: getInventoryCircuitPlanDetails,
            datatable: result.datatable,
        });
    },
    getCircuitOrderPlanDetails: (requestTypeId, orderId, orderCircuitDetailId = 0) => async (dispatch, state) => {
        var result = await httpGet('external/order/getCircuitOrderPlanDetails/' + requestTypeId + '/' + orderId + '/' + orderCircuitDetailId);

        dispatch({
            type: getCircuitOrderPlanDetails,
            datatable: result.datatable,
        });
    },

    getInventoryPlanTypesByVendorByCircuitTypeAsync: (vendorId, circuitTypeId = 0, page = 1, searchString = '') => async (dispatch, state) => {
        const pageSize = 15;
        const { circuitID } = state().form.inventoryCircuitForm.circuitDetails;

        if (state().inventoryCircuitPlanDetails.isCircuitPlansLoading) return;

        dispatch({ type: setPlanLoading, payload: true });

        let url = `external/inventory/getInventoryPlanByVendorByCircuitTypeAsync/${vendorId}/${circuitTypeId}/${circuitID}/${page}/${pageSize}`;
        if (searchString) {
            const cleanSearch = searchString.replace(/\(.*?\)/g, "").trim();
            url += `?searchString=${encodeURIComponent(cleanSearch)}`;
        }
        try {
            const res = await httpGet(url);

            if (res) {
                const mappedPlans = res.map(plan => {
                    let statusText = plan.UniquePlanIdentifier.includes("_InUse")
                        ? "In-Use"
                        : plan.PlanNameOrderable.includes("Orderable")
                            ? "Orderable"
                            : "Non-Orderable";

                    return {
                        ...plan,
                        PlanNameOrderable: `${plan.PlanName} (${statusText})`
                    };
                });

                dispatch({
                    type: getInventoryPlanTypesByVendorByCircuitType,
                    payload: { plans: mappedPlans, planPage: page }
                });
                dispatch({ type: setPlanLoading, payload: false });
            }
        } catch (err) {
            dispatch({ type: setPlanLoading, payload: false });
        } finally {
            dispatch({ type: setPlanLoading, payload: false });
        }
    },
    getInventoryCircuitPlanComponents: (planId, isOriginalComponent) => async (dispatch, state) => {
        const { circuitID } = state().form.inventoryCircuitForm.circuitDetails;
        const { checkedItems } = state().inventoryCircuitPlanDetails;
        const checkedIds = new Set(checkedItems.map(item => item.PlanComponentId));
        let planItems;

        try {
            if (planId && planId !== 0) {
                dispatch(actions.change('inventoryCicuitPlanDetailsForm.PlanId', planId));

                if (isOriginalComponent) {
                    dispatch(actions.change('inventoryCicuitPlanDetailsForm.UniquePlanIdentifier', planId + '_InUse'));
                    planItems = await httpGet(`external/inventory/getInventoryCircuitPlanComponent/${planId}/${circuitID}`);
                } else {
                    dispatch(actions.change('inventoryCicuitPlanDetailsForm.UniquePlanIdentifier', planId + '_Orderable'));
                    planItems = await httpGet(`external/inventory/getInventoryPlanComponentByPlan/${planId}`);
                }

                const updatedItems = planItems.Data?.Items.map(item => ({
                    ...item,
                    isChecked: checkedIds.has(item.PlanComponentId),
                }));

                const updatedCheckboxState = updatedItems.reduce((acc, item) => {
                    acc[item.PlanComponentId] = item.isChecked;
                    return acc;
                }, {});

                dispatch({
                    type: getInventoryCircuitPlanComponents,
                    planComponents: [
                        {
                            HeaderItem: [planItems.Headers],
                            dataItem: planItems.Data.Items
                        }
                    ],
                    checkedItems: updatedItems,
                    updatedCheckboxState,
                });
            } else {
                dispatch(actions.change('inventoryCicuitPlanDetailsForm.PlanId', ''));
                dispatch(actions.change('inventoryCicuitPlanDetailsForm.UniquePlanIdentifier', ''));
                dispatch({
                    type: getInventoryCircuitPlanComponents,
                    planComponents: [{ HeaderItem: [], dataItem: [] }],
                    checkedItems: []
                });
            }
        } catch (error) {
            console.error('Error fetching plan components:', error);
            dispatch({
                type: getInventoryCircuitPlanComponents,
                planComponents: [{ HeaderItem: [], dataItem: [] }],
                checkedItems: []
            });
        }
    },
    deletePlanDetails: () => (dispatch, state) => {
        const { datatable } = state().inventoryCircuitPlanDetails.planDetails;
        dispatch(actions.change('inventoryCicuitPlanDetailsForm.PlanId', ''));
        dispatch(actions.change('inventoryCicuitPlanDetailsForm.UniquePlanIdentifier', ''));
        const newDatatable = [
            {
                HeaderItem: datatable[0].HeaderItem,
                dataItem: []
            }
        ];

        dispatch({
            type: deleteInventoryCircuitPlanDetails,
            datatable: newDatatable,
            checkedItems: []
        });
    },
    addPlanDetails: () => (dispatch, state) => {
        const { circuitPlans, planDetails, checkedItems } = state().inventoryCircuitPlanDetails;
        const { PlanId, UniquePlanIdentifier } = state().form.inventoryCicuitPlanDetailsForm;
        const { dataItem } = state().inventoryCircuitPlanDetails.planComponents[0];

        const selectedPlan = circuitPlans.filter((r) => r.PlanId == PlanId);
        const totalCost = (typeof UniquePlanIdentifier === 'string' && UniquePlanIdentifier.includes('_InUse')) ?
            dataItem.filter((r) => r.PlanId == PlanId).reduce((sum, item) => sum + (item.ComponentCost || 0), 0) :
            checkedItems?.filter((r) => r.PlanId == PlanId).reduce((sum, item) => sum + (item.ComponentCost || 0), 0);

        if (selectedPlan.length > 0) {
            const dataItem = [{
                Currency: null,
                Description: null,
                Monthly: totalCost,
                PlanComponentName: null,
                PlanComponents: checkedItems,
                PlanId: PlanId,
                PlanName: selectedPlan[0].PlanName,
                UniquePlanIdentifier: UniquePlanIdentifier, // Use updated identifier
                PlanType: null,
                Status: selectedPlan[0].Status,
                TotalCost: 0,
                Year: 0
            }];

            const newDatatable = [
                {
                    HeaderItem: planDetails.datatable[0].HeaderItem,
                    dataItem: dataItem
                }
            ];
            dispatch({
                type: addInventoryCircuitPlanDetails,
                datatable: newDatatable,
            });
        }
    },
    resetPlanDetails: () => (dispatch, state) => {
        dispatch({
            type: resetInventoryCircuitPlanDetails,
            defaultState: defaultState()
        });
    },
    saveCheckedPlanComponents: (checkedItems) => (dispatch) => {
        const updatedCheckboxState = checkedItems.reduce((acc, item) => {
            acc[item.PlanComponentId] = true;
            return acc;
        }, {});
        dispatch({
            type: saveCheckedInventoryCircuitPlanComponents,
            checkedItems,
            updatedCheckboxState
        });
    }
};
export const reducer = (state, action) => {
    state = state || initialState;

    switch (action.type) {
        case getInventoryCircuitPlanDetails:
            return {
                ...state,
                planDetails: {
                    ...state.planDetails,
                    datatable: action.datatable
                }
            };
        case getCircuitOrderPlanDetails:
            return {
                ...state,
                planDetails: {
                    ...state.planDetails,
                    datatable: action.datatable
                }
            };
        case getInventoryPlanTypesByVendorByCircuitType:
            const { plans, planPage } = action.payload;
            return {
                ...state,
                circuitPlans: planPage === 1
                    ? plans
                    : [...state.circuitPlans, ...plans],
                hasMoreCircuitPlans: plans.length > 0,
                circuitPlansPage: planPage
            };
        case setPlanLoading:
            return {
                ...state,
                isCircuitPlansLoading: action.payload
            };
        case getInventoryCircuitPlanComponents:
            return {
                ...state,
                planComponents: action.planComponents, 
                checkedItems: action.checkedItems || [], 
                updatedCheckboxState: action.updatedCheckboxState || [], 
            };
        case deleteInventoryCircuitPlanDetails:
            return {
                ...state,
                planDetails: {
                    ...state.planDetails,
                    datatable: action.datatable
                },
                checkedItems: action.checkedItems || [],
            };
        case addInventoryCircuitPlanDetails:
            return {
                ...state,
                planDetails: {
                    ...state.planDetails,
                    datatable: action.datatable
                }
            };
        case resetInventoryCircuitPlanDetails:
            return action.defaultState;
        case saveCheckedInventoryCircuitPlanComponents:
            return {
                ...state,
                checkedItems: action.checkedItems,
                updatedCheckboxState: action.updatedCheckboxState || [],
            };
        default:
            return state;
    }
};
